import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Languages, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteSettings } from '../hooks/use-site-settings';
import { useLanguage } from '../i18n/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [hideMobileContactBar, setHideMobileContactBar] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(() => Boolean(sessionStorage.getItem('admin_services_password')));
  const { siteSettings } = useSiteSettings();
  const { language, setLanguage, languageOptions, tr, localizeText } = useLanguage();
  const location = useLocation();
  const pageEditor = (() => {
    if (location.pathname === '/') return { tab: 'homePage', label: 'Edit Home Page' };
    if (location.pathname === '/contact') return { tab: 'contactPage', label: 'Edit Contact Page' };
    if (location.pathname === '/booking') return { tab: 'bookingPage', label: 'Edit Booking Page' };
    if (location.pathname === '/about') return { tab: 'about', label: 'Edit About Page' };
    if (location.pathname === '/services') return { tab: 'servicesPage', label: 'Edit Services Page' };
    if (location.pathname.startsWith('/services/')) return { tab: 'servicesLabels', label: 'Edit Service Details' };
    return { tab: 'homePage', label: 'Edit Website' };
  })();
  const pageEditorUrl = `/admin/content?tab=${pageEditor.tab}`;

  React.useEffect(() => {
    const MOBILE_BREAKPOINT = 768;
    const SCROLL_THRESHOLD = 16;

    const updateContactBarVisibility = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      const isScrolled = window.scrollY > SCROLL_THRESHOLD;
      setHideMobileContactBar(isMobile && isScrolled);
    };

    updateContactBarVisibility();
    window.addEventListener('scroll', updateContactBarVisibility, { passive: true });
    window.addEventListener('resize', updateContactBarVisibility);

    return () => {
      window.removeEventListener('scroll', updateContactBarVisibility);
      window.removeEventListener('resize', updateContactBarVisibility);
    };
  }, []);

  React.useEffect(() => {
    const updateAdminState = () => setIsAdmin(Boolean(sessionStorage.getItem('admin_services_password')));
    window.addEventListener('focus', updateAdminState);
    return () => window.removeEventListener('focus', updateAdminState);
  }, []);

  const navLinks = [
    { name: tr('nav.home'), path: '/' },
    { name: tr('nav.about'), path: '/about' },
    { name: tr('nav.services'), path: '/services' },
    { name: tr('nav.bookAppointment'), path: '/booking' },
    { name: tr('nav.contact'), path: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-sage-200 shadow-sm">
      <div
        className={`bg-sage-50 overflow-hidden transition-all duration-300 ${
          hideMobileContactBar
            ? 'max-h-0 opacity-0 border-b-0 md:max-h-40 md:opacity-100 md:border-b md:border-sage-200'
            : 'max-h-40 opacity-100 border-b border-sage-200'
        }`}
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4 text-sm text-sage-700">
            <a
              href={siteSettings.contactPhoneHref}
              className="flex items-center gap-1.5 hover:text-sage-900 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>{siteSettings.contactPhone}</span>
            </a>
            <a
              href={`mailto:${siteSettings.contactEmail}`}
              className="flex items-center gap-1.5 hover:text-sage-900 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{siteSettings.contactEmail}</span>
            </a>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              <span>{localizeText(siteSettings.locationLabel)}</span>
            </div>
            <label className="flex items-center gap-1.5">
              <Languages className="w-3.5 h-3.5" />
              <span>{tr('lang.label')}</span>
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="rounded border border-sage-300 bg-white px-2 py-1 text-xs text-sage-800"
                aria-label={tr('lang.label')}
              >
                {languageOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-sage-500 to-earth-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">{siteSettings.brandInitials}</span>
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sage-900 text-lg leading-tight">
                {localizeText(siteSettings.businessName)}
              </span>
              <span className="text-xs text-sage-600">{localizeText(siteSettings.businessTagline)}</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-4 py-2 text-sage-700 hover:text-sage-900 hover:bg-sage-50 rounded-lg transition-all duration-200 font-medium"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:block">
            <div className="flex items-center gap-2">
              {isAdmin && <Link to={pageEditorUrl}><Button variant="outline" className="border-sage-300"><Pencil className="mr-2 h-4 w-4" />{pageEditor.label}</Button></Link>}
              <Link to="/booking"><Button className="bg-sage-600 hover:bg-sage-700 text-white shadow-md hover:shadow-lg transition-all duration-200">{tr('action.bookSession')}</Button></Link>
            </div>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-sage-50 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-sage-700" />
            ) : (
              <Menu className="w-6 h-6 text-sage-700" />
            )}
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-sage-200 animate-in slide-in-from-top">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-sage-700 hover:text-sage-900 hover:bg-sage-50 rounded-lg transition-colors font-medium"
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/booking" onClick={() => setIsMenuOpen(false)} className="px-4 pt-2">
                <Button className="w-full bg-sage-600 hover:bg-sage-700 text-white">
                  {tr('action.bookSession')}
                </Button>
              </Link>
              {isAdmin && <Link to={pageEditorUrl} onClick={() => setIsMenuOpen(false)} className="px-4 pt-2"><Button variant="outline" className="w-full border-sage-300"><Pencil className="mr-2 h-4 w-4" />{pageEditor.label}</Button></Link>}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
